/**
 * @file     ChatWorkspace.tsx
 * @purpose  The morphed homepage chat: history rail (desktop, signed-in),
 *           message thread with match cards, composer. The sign-up gate modal
 *           is injected by the owning view (renderGate) to keep layering clean.
 */
'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { useAuth } from '@/hooks/useAuth';
import { reltaTrack } from '@/hooks/useReltaTrack';
import LogoSvg from '@/components/layout/Navbar/LogoSvg';
import type { useReltaChat } from '../useReltaChat';
import { ReltaChatBar } from './ReltaChatBar';
import { MatchCards } from './MatchCards';
import type { MlsListingCardData } from '@/types/listing';
import { HistoryRail } from './HistoryRail';
import { TypewriterText } from './TypewriterText';
import { ProposalCard } from './ProposalCard';
import { RevertChip } from './RevertChip';
import type { TraditionalSearchValue } from '@/components/search/searchValue';
import { ChatChartCard } from './ChatChartCard';
import { useShellHeight, useAutoFollow, useEdgeLatch, useBodyScrollLock } from './useChatShell';

/** Rotating status lines while Relta works (voice-safe: no first person). */
const THINKING_KEYS: Array<[string, string]> = [
  ['aiChat.thinking1', 'Relta is looking'],
  ['aiChat.thinking2', 'Reading your message'],
  ['aiChat.thinking3', 'Checking the market'],
  ['aiChat.thinking4', 'Counting matches'],
  ['aiChat.thinking5', 'Sizing up neighbourhoods'],
  ['aiChat.thinking6', 'Weighing the fit'],
  ['aiChat.thinking7', 'Lining up homes'],
];

function ThinkingLine() {
  const { t } = useTranslation();
  const [i, setI] = useState(() => Math.floor(Math.random() * THINKING_KEYS.length));
  useEffect(() => {
    const id = setInterval(() => setI((v) => (v + 1) % THINKING_KEYS.length), 2200);
    return () => clearInterval(id);
  }, []);
  const [key, fallback] = THINKING_KEYS[i];
  return <>{t(key, fallback)}…</>;
}

type Chat = ReturnType<typeof useReltaChat>;

function ReltaAvatar({ border }: { border: string }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: 30,
        height: 30,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `1px solid ${border}`,
      }}
    >
      <LogoSvg height={17} />
    </Box>
  );
}

function UserAvatar({ initial, brand, textOnBrand }: { initial: string | null; brand: string; textOnBrand: string }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: 30,
        height: 30,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: brand,
        color: textOnBrand,
        fontSize: 13,
        fontWeight: 700,
      }}
    >
      {initial ?? (
        <svg viewBox="0 0 24 24" fill="none" style={{ width: 16, height: 16 }}>
          <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.7" />
          <path d="M19 20c0-2.8-3.1-5-7-5s-7 2.2-7 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      )}
    </Box>
  );
}

export function ChatWorkspace({
  chat,
  renderGate,
  embedded = false,
  onOpenListing,
  welcomeMessage,
  composerPlaceholder,
  composerAriaLabel,
  composerSuggestions,
  autoFocusComposer = true,
  gateNote,
  gateCta,
  disclaimer,
}: {
  chat: Chat;
  renderGate?: (open: boolean, onClose: () => void) => ReactNode;
  /** Embedded surface (e.g. the /properties AI Search panel): one persistent
   *  conversation, no history rail / drawer / new-search, fills its container. */
  embedded?: boolean;
  /** Host handler for a card tap (e.g. focus the map + open the detail). When
   *  set, cards call this instead of navigating to the listing page. */
  onOpenListing?: (listing: MlsListingCardData) => void;
  /** Visually typed assistant greeting for a genuinely empty conversation.
   *  It is presentation only and never enters model history. */
  welcomeMessage?: string;
  composerPlaceholder?: string;
  composerAriaLabel?: string;
  composerSuggestions?: string[];
  autoFocusComposer?: boolean;
  gateNote?: string;
  gateCta?: string;
  disclaimer?: string;
}) {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const { user } = useAuth();
  const meta = (user?.user_metadata ?? {}) as { first_name?: string };
  const initial = (meta.first_name?.[0] ?? user?.email?.[0])?.toUpperCase() ?? null;
  const threadRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  // The gate flag ("reached the sign-up moment") outlives the modal: the only
  // extra bit is whether the user dismissed it, so derive instead of syncing.
  const [gateDismissed, setGateDismissed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Embedded (/properties) previous-chats pill: the history is hidden until the
  // pill is tapped, keeping the panel clean.
  const [histAnchor, setHistAnchor] = useState<HTMLElement | null>(null);
  const previousChats = embedded ? chat.recentConversations.filter((c) => c.id !== chat.localId) : [];
  const modalOpen = chat.gateOpen && !chat.isLoggedIn && !gateDismissed;
  const streamingIdx = chat.streamIdx;

  const shellHeight = useShellHeight(shellRef);
  const { atBottom, scrollToBottom } = useAutoFollow(threadRef, {
    messageCount: chat.messages.length,
    thinking: chat.thinking,
    streaming: streamingIdx !== null,
  });
  useEdgeLatch(threadRef);
  // iOS Safari keyboard fix: while the chat fills a mobile screen, lock the
  // document so focusing the composer can't make Safari pan the page up.
  const isNarrow = useMediaQuery('(max-width: 899px)');
  // Embedded (the /properties map panel) locks the document for the whole
  // mobile session: the page is a fixed full-screen surface, so ANY input focus
  // (composer or filters) would otherwise pan it. The hero chat keeps its
  // open-gated lock. Restored on unmount either way, so navigating away is clean.
  useBodyScrollLock((embedded || chat.open) && isNarrow);

  const gateBlocked = chat.gateOpen && !chat.isLoggedIn;

  return (
    <Box
      ref={shellRef}
      sx={{
        display: 'grid',
        gridTemplateColumns: embedded ? '1fr' : { xs: '1fr', md: '250px minmax(0, 1fr)' },
        gap: embedded ? 0 : { md: 4, xs: 2 },
        width: '100%',
        textAlign: 'left',
        // Standalone (hero): the shell owns the viewport via a measured height
        // (dvh fallback), so no state renders shorter/taller than the screen.
        // Embedded (map panel): the panel owns the height, so just fill it —
        // self-measuring here fought the panel and left the composer stranded
        // when the keyboard opened.
        height: embedded ? '100%' : shellHeight,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {!embedded && (
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          height: '100%',
          overflowY: 'auto',
          pr: 2,
          borderRight: `1px solid ${theme.semanticColors.borderSubtle}`,
        }}
      >
        <HistoryRail
          conversations={chat.conversations}
          activeId={chat.conversationId}
          signedIn={chat.isLoggedIn}
          onNew={chat.newConversation}
          onOpen={(id) => void chat.openConversation(id)}
          onDelete={(id) => void chat.removeConversation(id)}
        />
      </Box>
      )}

      {/* Chat column: full-width cell, readable measure centered inside it. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', maxWidth: embedded ? 'none' : 860, width: '100%', mx: 'auto', minHeight: 0, position: 'relative' }}>
        <Box sx={{ display: embedded ? 'none' : { xs: 'flex', md: 'none' }, justifyContent: 'flex-start', mb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setDrawerOpen(true)}
            startIcon={
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                <path d="M4 6h16M4 12h10M4 18h16" />
              </svg>
            }
            sx={{ borderRadius: 999, textTransform: 'none', fontSize: 13, px: 1.5, borderColor: theme.semanticColors.borderSubtle, color: theme.semanticColors.textPrimary }}
          >
            {t('aiChat.chats', 'Chats')}
          </Button>
        </Box>

        {/* Embedded: a quiet "Previous chats" pill. Tapping reveals the history;
            otherwise the log stays hidden so the panel is clean. */}
        {embedded && previousChats.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 1, flexShrink: 0 }}>
            <Button
              size="small"
              variant="text"
              onClick={(e) => setHistAnchor(e.currentTarget)}
              endIcon={
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M6 9l6 6 6-6" />
                </svg>
              }
              sx={{ borderRadius: 999, textTransform: 'none', fontSize: 12.5, py: 0.25, px: 1, minWidth: 0, color: theme.semanticColors.textSecondary }}
            >
              {t('aiChat.previousChats', 'Previous chats')}
            </Button>
            <Popover
              open={!!histAnchor}
              anchorEl={histAnchor}
              onClose={() => setHistAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { width: 'min(300px, 88vw)', maxHeight: '60vh', p: 2, borderRadius: 1, backgroundColor: theme.semanticColors.backgroundPrimary, border: `1px solid ${theme.semanticColors.borderSubtle}` } }}
            >
              <HistoryRail
                conversations={previousChats}
                activeId={chat.conversationId ?? chat.localId}
                signedIn={chat.isLoggedIn}
                onNew={() => { setHistAnchor(null); chat.newConversation(); }}
                onOpen={(id) => { setHistAnchor(null); void chat.openConversation(id); }}
                onDelete={(id) => void chat.removeConversation(id)}
              />
            </Popover>
          </Box>
        )}
        {!embedded && (
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { width: 'min(290px, 85vw)', p: 2, backgroundColor: theme.semanticColors.backgroundPrimary } }}
        >
          <HistoryRail
            conversations={chat.conversations}
            activeId={chat.conversationId}
            signedIn={chat.isLoggedIn}
            onNew={() => { setDrawerOpen(false); chat.newConversation(); }}
            onOpen={(id) => { setDrawerOpen(false); void chat.openConversation(id); }}
            onDelete={(id) => void chat.removeConversation(id)}
          />
        </Drawer>
        )}
        <Box
          ref={threadRef}
          role="log"
          aria-live="polite"
          aria-label={t('aiChat.threadLabel', 'Conversation with Relta')}
          sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', display: 'flex', flexDirection: 'column', px: 2, pt: 2, pb: 3 }}
        >
          {chat.messages.length === 0 && (
            welcomeMessage ? (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <ReltaAvatar border={theme.semanticColors.borderSubtle} />
                <Typography sx={{ pt: 0.25, fontSize: 15, lineHeight: 1.65, color: theme.semanticColors.textPrimary, whiteSpace: 'pre-wrap' }}>
                  <TypewriterText text={welcomeMessage} animate />
                </Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, textAlign: 'center', px: 2 }}>
                <Typography sx={{ fontSize: 22, fontWeight: 700, color: theme.semanticColors.textPrimary }}>
                  {t('aiChat.emptyTitle', 'Start a new search')}
                </Typography>
                <Typography sx={{ fontSize: 14.5, color: theme.semanticColors.textSecondary, maxWidth: 420 }}>
                  {t('aiChat.emptySub', 'Describe the home you want. Relta finds it and answers your questions.')}
                </Typography>
              </Box>
            )
          )}

          {chat.messages.map((m, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                // User turns mirror to the right, avatar outermost.
                flexDirection: m.role === 'user' ? 'row-reverse' : 'row',
                gap: 2,
                alignItems: 'flex-start',
                // Rhythm: a reply hugs its question; the next question starts
                // a new group with clear air above it.
                mt: i === 0 ? 0 : m.role === 'assistant' ? 2.25 : 5,
              }}
            >
              {m.role === 'user' ? (
                <UserAvatar initial={initial} brand={theme.palette.brand.primary} textOnBrand={theme.semanticColors.textOnPrimary} />
              ) : (
                <ReltaAvatar border={theme.semanticColors.borderSubtle} />
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'stretch', minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: m.role === 'user' ? '85%' : '100%',
                  px: m.role === 'user' ? 2 : 0,
                  py: m.role === 'user' ? 1 : 0.25,
                  borderRadius: 1,
                  backgroundColor: m.role === 'user' ? theme.semanticColors.surfaceCard : 'transparent',
                  border: m.role === 'user' ? `1px solid ${theme.semanticColors.borderSubtle}` : 'none',
                }}
              >
                <Typography sx={{ fontSize: 15, lineHeight: 1.65, color: theme.semanticColors.textPrimary, whiteSpace: 'pre-wrap' }}>
                  {m.role === 'assistant' ? (
                    <TypewriterText
                      text={m.content}
                      animate={i === streamingIdx}
                      onDone={chat.endStream}
                    />
                  ) : (
                    m.content
                  )}
                </Typography>
              </Box>

              {m.facts && i !== streamingIdx && (
                <Typography sx={{ mt: 2, fontSize: 14.5, lineHeight: 1.6, color: theme.semanticColors.textPrimary }}>
                  {m.facts}
                </Typography>
              )}

              {m.widgets && i !== streamingIdx && m.widgets.map((w) => <ChatChartCard key={w.title} widget={w} />)}

              {m.matches && i !== streamingIdx && <MatchCards matches={m.matches} reasons={m.reasons} onOpenListing={onOpenListing} />}

              {m.proposals && i !== streamingIdx && m.proposals.map((p) => <ProposalCard key={p.id} proposal={p} />)}

              {m.revertSuggestion && i !== streamingIdx && (
                <RevertChip
                  suggestion={m.revertSuggestion}
                  onRestore={() => chat.applyRevert(m.revertSuggestion!.filters as unknown as Partial<TraditionalSearchValue>)}
                />
              )}

              {m.agentCta && i !== streamingIdx && (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={chat.thinking}
                  onClick={() => {
                    reltaTrack('ai_chat.agent_cta_clicked', {});
                    void chat.send(t('aiChat.agentCtaMessage', 'I would like to talk with Ray about this'));
                  }}
                  sx={{ alignSelf: 'flex-start', mt: 2, borderRadius: 1, textTransform: 'none', fontSize: 13 }}
                >
                  {t('aiChat.agentCta', 'Talk to Ray, a licensed agent')}
                </Button>
              )}

              {m.mapsUrl && (
                <Button
                  href={m.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  sx={{ alignSelf: 'flex-start', mt: 1, borderRadius: 1, textTransform: 'none' }}
                >
                  {t('aiChat.openMaps', 'Check the commute on Google Maps')}
                </Button>
              )}

              {m.error && i === chat.messages.length - 1 && (
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={t('aiChat.retry', 'Try again')}
                    onClick={() => void chat.retry()}
                    disabled={chat.thinking}
                    sx={{
                      borderRadius: 1,
                      backgroundColor: 'transparent',
                      border: `1px solid ${theme.semanticColors.borderSubtle}`,
                      color: theme.semanticColors.textPrimary,
                      fontSize: 12.5,
                    }}
                  />
                </Box>
              )}

              </Box>
            </Box>
          ))}

          {chat.thinking && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ReltaAvatar border={theme.semanticColors.borderSubtle} />
              <CircularProgress size={16} thickness={5} />
              <Typography sx={{ fontSize: 13.5, color: theme.semanticColors.textSecondary }}>
                <ThinkingLine />
              </Typography>
            </Box>
          )}
        </Box>

        {!atBottom && (
          <Box
            component="button"
            type="button"
            onClick={() => scrollToBottom()}
            aria-label={t('aiChat.jumpToLatest', 'Jump to latest')}
            sx={{
              position: 'absolute',
              bottom: 'calc(96px + env(safe-area-inset-bottom, 0px))',
              left: '50%',
              transform: 'translateX(-50%)',
              minHeight: 32,
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontSize: 12.5,
              cursor: 'pointer',
              backgroundColor: theme.semanticColors.surfaceElevated,
              border: `1px solid ${theme.semanticColors.borderSubtle}`,
              color: theme.semanticColors.textSecondary,
              zIndex: 1,
              '&:focus-visible': { outline: `2px solid ${theme.palette.brand.primary}`, outlineOffset: 2 },
            }}
          >
            {t('aiChat.jumpToLatest', 'Jump to latest')} ↓
          </Box>
        )}
        <Box
          sx={{
            pt: 2,
            px: 2,
            // Keep the composer clear of the iOS home indicator / gesture bar.
            pb: 'calc(12px + env(safe-area-inset-bottom, 0px))',
            borderTop: `1px solid ${theme.semanticColors.borderSubtle}`,
          }}
        >
          {gateBlocked && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography sx={{ fontSize: 13, color: theme.semanticColors.textSecondary, textAlign: 'center' }}>
                {gateNote ?? t('aiChat.gateNote', 'Create your account to keep this search and unlock member listing data.')}
              </Typography>
              <Button variant="contained" size="small" onClick={() => setGateDismissed(false)} sx={{ borderRadius: 1, textTransform: 'none' }}>
                {gateCta ?? t('aiChat.gateCta', 'Continue with an account')}
              </Button>
            </Box>
          )}
          <ReltaChatBar
            onSend={chat.send}
            disabled={chat.thinking || gateBlocked}
            showSuggestions={chat.messages.length === 0}
            autoFocus={autoFocusComposer && chat.messages.length === 0}
            placeholder={composerPlaceholder}
            ariaLabel={composerAriaLabel}
            suggestions={composerSuggestions}
          />
          <Typography
            variant="caption"
            component="p"
            sx={{ textAlign: 'center', color: theme.semanticColors.textSecondary, fontSize: 11, mt: 1, mb: 0, lineHeight: 1.4 }}
          >
            {disclaimer ?? t('aiChat.disclaimer', {
              defaultValue: 'AI assistant. Replies are informational, can contain mistakes, and are not advice from a licensed agent.',
            })}
          </Typography>
        </Box>
      </Box>

      {renderGate?.(modalOpen, () => setGateDismissed(true))}
    </Box>
  );
}
